import { useState, useEffect, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useUpdateUser } from '@/hooks/useUsers';
import { ROLES, ROLE_LABELS, type AppUser, type Role } from '@/types';

type Props = {
  open: boolean;
  onClose: () => void;
  user: AppUser | null;
};

export function EditUserModal({ open, onClose, user }: Props) {
  const update = useUpdateUser();
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('staff');
  const [department, setDepartment] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName ?? '');
      setRole(user.role);
      setDepartment(user.department ?? '');
      setDisabled(user.disabled ?? false);
      setError(null);
    }
  }, [user]);

  if (!user) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!user) return;
    try {
      await update.mutateAsync({
        uid: user.uid,
        displayName: displayName.trim(),
        role,
        department: department.trim() || undefined,
        disabled,
      });
      toast.success('User updated');
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to update user.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="Edit User"
      title={user.displayName ?? user.email}
      description={user.email}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="edit-user-form" loading={update.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <Input
          label="Full name"
          name="displayName"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            name="role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
          />

          <Input
            label="Department"
            name="department"
            placeholder="Optional"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          />
        </div>

        <div className="pt-3 border-t border-border">
          <Switch
            checked={disabled}
            onChange={setDisabled}
            label="Disable account"
            description="A disabled account cannot sign in. The profile and history are preserved."
          />
        </div>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
